import { Injectable } from '@angular/core';

import { map, Observable, of, share } from 'rxjs';

import { Country, State } from '@reg/shared/model';
import { HttpServiceHelper } from '@reg/shared/service/http-service-helper';
import { IJsonApiResponse } from '../json-api/json-api.interface';
import { observableInSeparateThread } from '../helper/observable-helper';

/**
 * Service to get countries and their states
 */
@Injectable()
export class CountryService {
    private cachedCountries: Country[] = null;
    private cachedStatesMap: { [apiRequest: string]: { obs: Observable<State[]>, records: State[] } } = {};

    /**
     * Constructor
     */
    constructor(private http: HttpServiceHelper) {
    }

    /**
     * Get all countries
     */
    public getIndex(forceRefresh: boolean = false): Observable<Country[]> {
        if (!forceRefresh &&
            this.cachedCountries) {
            // if `cachedCountries` is available just return it as `Observable`
            return observableInSeparateThread(this.cachedCountries.map((x: Country) => x.clone()));
        }
        // Make the http request
        const apiUrl: string = `${this.http.configService.getApiBaseUrl()}/countries/`;

        return this.http.get(apiUrl)
            .pipe(map((response: IJsonApiResponse) => {
                const countries: Country[] = [];
                const bodyData: any = response.data || [];
                for (const country of bodyData) {
                    countries.push((new Country()).deserialize(country));
                }
                this.cachedCountries = countries;
                return this.cachedCountries.map((x: Country) => x.clone());
            }));
    }

    /**
     * Get states for a given country
     */
    public getStates(countryId: string, forceRefresh: boolean = false): Observable<State[]> {
        const apiUrl: string = `${this.http.configService.getApiBaseUrl()}/countries/${countryId}/states/`;

        if (!forceRefresh &&
            this.cachedStatesMap[apiUrl]) {
            if (this.cachedStatesMap[apiUrl].records) {
                return observableInSeparateThread(
                    this.cachedStatesMap[apiUrl].records.map((s: State) => s.clone()),
                );
            }
            return this.cachedStatesMap[apiUrl].obs;
        }
        // Make the http request
        this.cachedStatesMap[apiUrl] = { obs: null, records: null };

        this.cachedStatesMap[apiUrl].obs = this.http.get(apiUrl)
            .pipe(
                map((response: IJsonApiResponse) => {
                    const states: State[] = [];
                    const bodyData: any = response.data || [];
                    for (const state of bodyData) {
                        states.push((new State()).deserialize(state));
                    }
                    // this.http.cachedCountryStates = states;
                    this.cachedStatesMap[apiUrl].records = states;
                    return states.map((x: State) => x.clone());
                }),
                share(),
            );

        return this.cachedStatesMap[apiUrl].obs;
    }
}
